import { useCallback } from 'react'
import { useSearchParams } from 'react-router'
import type { PricingView, BillingPeriod } from '@/components/manager/subscription/plans'

export type Currency = 'cad' | 'usd'

const VIEW_VALUES: readonly PricingView[] = ['independants', 'equipes', 'enterprise'] as const
const BILLING_VALUES: readonly BillingPeriod[] = ['monthly', 'yearly'] as const
const CURRENCY_VALUES: readonly Currency[] = ['cad', 'usd'] as const

function parseEnum<T extends string>(
  raw: string | null,
  allowed: readonly T[],
  fallback: T,
): T {
  return raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback
}

/**
 * Hook URL state pour la page Subscribe.
 * Param noms : `vue`, `facturation`, `devise` (cohérent avec /pricing publique Next).
 */
export function usePricingParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  const view = parseEnum(searchParams.get('vue'), VIEW_VALUES, 'independants')
  const billing = parseEnum<BillingPeriod>(
    searchParams.get('facturation') === 'annuel'
      ? 'yearly'
      : searchParams.get('facturation') === 'mensuel'
        ? 'monthly'
        : searchParams.get('facturation'),
    BILLING_VALUES,
    'yearly',
  )
  const currency = parseEnum<Currency>(searchParams.get('devise'), CURRENCY_VALUES, 'cad')

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams)
      next.set(key, value)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  return {
    view,
    billing,
    currency,
    setView: (v: PricingView) => setParam('vue', v),
    setBilling: (b: BillingPeriod) =>
      setParam('facturation', b === 'yearly' ? 'annuel' : 'mensuel'),
    setCurrency: (c: Currency) => setParam('devise', c),
  }
}
